import { useState, useEffect } from "react";
import { abmeldenZiel } from "@/lib/app/umgebung";
import { safeGetItem, safeKey, safeLength } from "@/lib/safeStorage";
import { UEBERNAHME_MARKE } from "@/lib/uebernahme-speicher";
import { useFachkraftGraduateAccess, isGraduateUnlockedArea } from "@/hooks/useFachkraftGraduateAccess";
import { safeSessionGetItem, safeSessionRemoveItem } from "@/lib/safeStorage";
import { SidebarProvider, Sidebar, SidebarContent, SidebarHeader, SidebarMenu, SidebarMenuItem, SidebarMenuButton, SidebarFooter, SidebarSeparator, useSidebar, SidebarMenuSub, SidebarMenuSubItem, SidebarMenuSubButton, SidebarTrigger } from "@/components/ui/sidebar";
import { BookOpen, Heart, Settings, Home, ChevronLeft, Shield, Search, icons, Loader2, LogOut, Award, MessageCircle, Wrench, Library, ChevronDown, Music, Sparkles, Play, HelpCircle, Calendar, MoreHorizontal, Lock, Users, ExternalLink, Info, Settings2, MapPinned, Compass, Handshake, Map as MapIcon } from "lucide-react";
import musikKitaBadge from "@/assets/musik-kita/musik-kita-badge.png";
import mikoCharacter from "@/assets/miko-character.png";
import musikonzeptLogo from "@/assets/musikonzept-logo.png";
import iconSearch from "@/assets/icons/icon-search.png";
import badgeAppStore from "@/assets/badge-app-store.svg";
import badgeGooglePlay from "@/assets/badge-google-play.png";
import { useIsNativeApp } from "@/hooks/useIsNativeApp";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Session } from "@supabase/supabase-js";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAccess } from "@/contexts/AccessContext";
import { navigateToCheckout } from "@/lib/externalBrowser";
import { useUnreadCommunityPosts } from "@/hooks/useUnreadCommunityPosts";
import { MikoFloatingBubble } from "@/components/student/MikoFloatingBubble";
import { useSubscriptionContext } from "@/contexts/SubscriptionContext";
import { useOpenTickets } from "@/hooks/useOpenTickets";
import { useDidactaTracking } from "@/hooks/useDidactaTracking";
import { applyLearningAreaOverride } from "@/config/learningAreaOverrides";
import { loginMitRueckwegHierher } from "@/lib/loginRueckweg";
import { NeueAnsicht, NeueAnsichtKnopf } from "@/components/labs/neueAnsicht/NeueAnsichtSchalter";
import { useNeueAnsicht } from "@/components/labs/neueAnsicht/useNeueAnsicht";

// Tier Badge Component for mobile header
const TierBadge = () => {
  const subscription = useSubscriptionContext();
  const currentTier = subscription.tier || 'start';
  
  if (subscription.isLoading) return null;
  
  return (
    <div className="md:hidden flex items-center px-2.5 py-1.5 rounded-full bg-gradient-to-r from-primary/10 to-accent/10 border border-border/50">
      <span className="text-xs font-semibold text-foreground">
        {currentTier === 'start' ? 'Basis' : 'Mitglied'}
      </span>
    </div>
  );
};

// Welcome Info Popover Component with tier-specific content
const WelcomeInfoPopover = () => {
  const subscription = useSubscriptionContext();
  const currentTier = subscription.tier || 'start';
  
  const getWelcomeContent = () => {
    if (currentTier === 'start') {
      return {
        title: "Herzlich willkommen bei MusiKonzept!",
        description: "MusiKonzept ist deine Online-Akademie für musikalische Früherziehung. Hier findest du praxiserprobte Konzepte, kreative Ideen und inspirierende Fortbildungen für den Einsatz von Musik in Kita, Grundschule und Musikschule.",
        tierInfo: "Du nutzt aktuell den kostenlosen Basis-Tarif und hast Zugriff auf ausgewählte Kurse sowie unseren KI-Assistenten Miko zum Ausprobieren. Für den vollen Zugang zu über 50 Kursen, allen Kreativpaketen und unbegrenzter KI-Nutzung kannst du jederzeit Mitglied werden."
      };
    } else {
      return {
        title: "Willkommen zurück!",
        description: "Mit deinem Pro-Abo hast du uneingeschränkten Zugang zu allen Kursen, Lernbereichen, Kreativpaketen und Premium-Features.",
        tierInfo: "Nutze alle Features unbegrenzt und gestalte deine musikalische Arbeit mit unseren vielfältigen Ressourcen."
      };
    }
  };
  
  const content = getWelcomeContent();
  
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm"
          className="h-10 w-10 p-0"
        >
          <Info className="h-5 w-5 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4" align="end">
        <div className="space-y-3">
          <h4 className="font-semibold text-foreground">{content.title}</h4>
          <p className="text-sm text-muted-foreground">{content.description}</p>
          <p className="text-sm text-muted-foreground">{content.tierInfo}</p>
        </div>
      </PopoverContent>
    </Popover>
  );
};
interface LearningArea {
  id: string;
  title: string;
  slug: string;
  icon: string;
  color: string;
}

// Dynamic icon component for learning areas
const DynamicIcon = ({ name, className }: { name: string; className?: string }) => {
  const IconComponent = icons[name as keyof typeof icons];
  if (!IconComponent) {
    return <BookOpen className={className} />;
  }
  return <IconComponent className={className} />;
};

interface StudentLayoutProps {
  children: React.ReactNode;
}

/**
 * Dezente App-Store-Badges im Sidebar-Footer.
 * Im collapsed-State und in der nativen App ausgeblendet.
 */
const AppStoreBadges = ({ isCollapsed }: { isCollapsed: boolean }) => {
  const isNativeApp = useIsNativeApp();
  if (isCollapsed || isNativeApp) return null;
  return (
    <div className="px-2 pt-1 pb-2 flex flex-col gap-1.5">
      <span className="text-[10px] font-medium text-muted-foreground/70 uppercase tracking-wider">App laden</span>
      <div className="flex items-center gap-1.5">
        <a
          href="https://apps.apple.com/de/app/musikonzept/id6755689687"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:opacity-80 transition-opacity flex-shrink-0"
          aria-label="Im App Store laden"
        >
          <img src={badgeAppStore} alt="App Store" className="h-6 w-auto" />
        </a>
        <a
          href="https://play.google.com/store/apps/details?id=com.wnapp.id1762014785410"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:opacity-80 transition-opacity flex-shrink-0"
          aria-label="Bei Google Play laden"
        >
          <img src={badgeGooglePlay} alt="Google Play" className="h-6 w-auto" />
        </a>
      </div>
    </div>
  );
};

const StudentSidebar = () => {
  const { state } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const isCollapsed = state === "collapsed";
  
  // Use AccessContext for tier-based access
  const { effectiveBundleEnrollments, tier, isAdmin } = useAccess();
  const { isFachkraftGraduate } = useFachkraftGraduateAccess();
  const openTicketCount = useOpenTickets();

  // Check if user has access to Musik-Kita bundle
  const MUSIK_KITA_BUNDLE_ID = 'b7e8f9a0-1234-5678-9abc-def012345678';
  const hasMusikKitaAccess = isAdmin || effectiveBundleEnrollments.includes(MUSIK_KITA_BUNDLE_ID);

  // Fetch user data for avatar - unified key for cache sharing
  const { data: user } = useQuery({
    queryKey: ["current-user"],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      return session?.user ?? null;
    },
    staleTime: 10 * 60 * 1000,
  });

  const { data: profile } = useQuery({
    queryKey: ["user-profile-sidebar", user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data } = await supabase
        .from("profiles")
        .select("first_name, last_name")
        .eq("user_id", user.id)
        .single();
      return data;
    },
    enabled: !!user,
  });

  // Zuletzt geöffnete Lektion für „Weitermachen“. StudentCourseView legt je Kurs
  // `lastLesson_<kursId>` ab ({ courseSlug, courseTitle, lessonId, lessonTitle,
  // timestamp }); der jüngste Eintrag gewinnt. Der früher gelesene Schlüssel
  // `lastLesson` wird nirgends mehr geschrieben, der Knopf blieb deshalb unsichtbar.
  const [lastLesson, setLastLesson] = useState<{
    courseSlug: string;
    courseTitle: string;
    lessonId: string;
    lessonTitle: string;
  } | null>(null);

  useEffect(() => {
    let juengste: { ts: number; courseSlug: string; courseTitle: string; lessonId: string; lessonTitle: string } | null = null;
    for (let i = 0; i < safeLength(); i++) {
      const key = safeKey(i);
      if (!key?.startsWith("lastLesson_")) continue;
      const stored = safeGetItem(key);
      if (!stored) continue;
      try {
        const d = JSON.parse(stored);
        if (typeof d?.courseSlug !== "string" || typeof d?.lessonId !== "string") continue;
        const ts = Number(d.timestamp) || 0;
        if (juengste && ts <= juengste.ts) continue;
        juengste = {
          ts,
          courseSlug: d.courseSlug,
          courseTitle: typeof d.courseTitle === "string" ? d.courseTitle : "",
          lessonId: d.lessonId,
          lessonTitle: typeof d.lessonTitle === "string" && d.lessonTitle ? d.lessonTitle : d.courseTitle || "Letzte Lektion",
        };
      } catch {
        // Invalid JSON, ignore
      }
    }
    if (juengste) {
      const { ts: _ts, ...rest } = juengste;
      setLastLesson(rest);
    }
  }, []);

  const weitermachenPfad = lastLesson
    ? `/mein-bereich/kurs/${encodeURIComponent(lastLesson.courseSlug)}?lektion=${encodeURIComponent(lastLesson.lessonId)}`
    : null;

  // Fetch ALL active learning areas with their bundle associations (excluding is_special ones like Musik-Kita)
  const { data: allLearningAreas } = useQuery({
    queryKey: ["all-learning-areas-with-bundles"],
    queryFn: async () => {
      const { data: areas, error: areasError } = await supabase
        .from("learning_areas")
        .select(`
          id, title, slug, icon, color, sort_order,
          learning_area_bundles(bundle_id, bundles(id, tier_level))
        `)
        .eq("is_active", true)
        .or("is_special.is.null,is_special.eq.false")
        .order("sort_order", { ascending: true });

      if (areasError) {
        console.error("Error fetching learning areas:", areasError);
        return [];
      }

      return (areas || []).map((a) => applyLearningAreaOverride(a));
    },
  });

  // Combine: all areas with access info - now using tier-based logic
  // IMPORTANT: Training bundles (tier_level: 'none') require explicit enrollment, even for Pro users
  const learningAreasWithAccess = allLearningAreas?.map(area => {
    const bundles = area.learning_area_bundles || [];
    
    // Check if this is a training-only area (all linked bundles have tier_level: 'none')
    const isTrainingOnlyArea = bundles.length > 0 && bundles.every((lab: any) => 
      lab.bundles?.tier_level === 'none' || lab.bundles?.tier_level === null
    );
    
    // Admins always have access
    if (isAdmin) {
      return { ...area, hasAccess: true };
    }
    
    // Absolventen behalten dauerhaft Zugriff auf ihre Ausbildungsinhalte
    if (isFachkraftGraduate && isGraduateUnlockedArea(area.slug)) {
      return { ...area, hasAccess: true };
    }

    
    // Pro users have access to non-training areas
    if (tier === 'pro' && !isTrainingOnlyArea) {
      return { ...area, hasAccess: true };
    }
    
    // For training areas or Start users: check explicit bundle enrollments
    const hasAccess = bundles.some((lab: any) => 
      effectiveBundleEnrollments.includes(lab.bundle_id)
    );
    
    return { ...area, hasAccess };
  }) || [];

  // Get initials for avatar
  const getInitials = () => {
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name[0]}${profile.last_name[0]}`.toUpperCase();
    }
    if (profile?.first_name) {
      return profile.first_name.slice(0, 2).toUpperCase();
    }
    if (user?.email) {
      return user.email.slice(0, 2).toUpperCase();
    }
    return "?";
  };

  const firstName = profile?.first_name || user?.email?.split("@")[0] || "";

  const menuItems = [
    { icon: Home, label: "Übersicht", path: "/mein-bereich", tourId: "nav-overview" },
    { icon: BookOpen, label: "Meine Kurse", path: "/mein-bereich/kurse", tourId: "nav-courses" },
    { icon: Compass, label: "Themenreisen", path: "/mein-bereich/themenreisen", tourId: "nav-themenreisen" },
    { icon: Calendar, label: "Meine Termine", path: "/mein-bereich/termine", tourId: "nav-termine" },
    { icon: Search, label: "Suche", path: "/mein-bereich/suche", tourId: "nav-search" },
    { icon: Heart, label: "Favoriten & Notizen", path: "/mein-bereich/favoriten", tourId: "nav-favorites" },
    { icon: Users, label: "Community", path: "/mein-bereich/community", tourId: "nav-community" },
  ];

  const accountMenuItems = [
    { icon: MapIcon, label: "Meine Lernreise", path: "/mein-bereich/lernreise", tourId: "nav-journey" },
    { icon: Award, label: "Meine Zertifikate", path: "/mein-bereich/zertifikate", tourId: "nav-certificates" },
    { icon: Handshake, label: "Partnerprogramm", path: "/mein-bereich/partner", tourId: "nav-partner" },
    { icon: HelpCircle, label: "Hilfe & Support", path: "/mein-bereich/hilfe", tourId: "nav-help" },
    { icon: Settings, label: "Mein Account", path: "/mein-bereich/einstellungen", tourId: "nav-settings" },
  ];



  return (
    <Sidebar collapsible="icon" className="student-sidebar-glass border-r border-primary/15 [&_.sidebar-content]:gap-1 group-data-[collapsible=icon]:items-center">
      <SidebarHeader className="p-3">
        {/* Brand-Karte */}
        <div className={cn(isCollapsed ? "flex justify-center" : "sidebar-brand-card relative")}>
          {isCollapsed ? (
            <NavLink to="/mein-bereich" className="block">
              <div className="w-10 h-10 rounded-xl overflow-hidden bg-gradient-to-br from-primary/15 to-accent/10 border border-primary/15 flex items-center justify-center">
                <img src={musikonzeptLogo} alt="MusiKonzept" className="h-6 w-6 object-contain" />
              </div>
            </NavLink>
          ) : (
            <>
              <NavLink to="/mein-bereich" className="flex items-center justify-center">
                <img src={musikonzeptLogo} alt="MusiKonzept" className="h-12 w-auto object-contain" />
              </NavLink>
              <a
                href="/"
                className="absolute top-2 right-2 flex items-center gap-1 text-[10px] text-muted-foreground hover:text-primary transition-colors px-1.5 py-1 rounded-md hover:bg-primary/5"
              >
                <ChevronLeft className="w-3 h-3" />
                <span>Web</span>
              </a>
            </>
          )}
        </div>


        {/* Continue Learning Button */}
        {lastLesson && !isCollapsed && (
          <button
            onClick={() => weitermachenPfad && navigate(weitermachenPfad)}
            className="mt-3 w-full flex items-center gap-2 p-2.5 rounded-2xl bg-gradient-to-r from-primary/10 via-primary/5 to-accent/5 border border-primary/15 hover:border-primary/30 hover:from-primary/15"
          >
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-md">
              <Play className="w-4 h-4 text-primary-foreground fill-current" />
            </div>
            <div className="flex-1 min-w-0 text-left">
              <p className="text-[10px] text-muted-foreground">Weitermachen</p>
              <p className="text-xs font-medium text-foreground truncate">{lastLesson.lessonTitle}</p>
            </div>
          </button>
        )}

        {/* Collapsed continue button */}
        {lastLesson && isCollapsed && (
          <button
            onClick={() => weitermachenPfad && navigate(weitermachenPfad)}
            className="mt-3 w-11 h-11 mx-auto rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-lg shadow-primary/40 transition-all duration-300 hover:scale-[1.15] hover:shadow-lg"
            title={`Weiter: ${lastLesson.lessonTitle}`}
          >
            <Play className="w-5 h-5 text-primary-foreground fill-current" />
          </button>
        )}
      </SidebarHeader>


      <SidebarContent className={cn("p-2 gap-0", isCollapsed && "items-center")}>
        {/* Main Navigation */}
        <div className={cn(!isCollapsed && "sidebar-section-card")}>
        <SidebarMenu className={cn("space-y-0.5", isCollapsed && "items-center")}>

          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            const isComingSoon = 'comingSoon' in item && item.comingSoon;
            
            if (isComingSoon) {
              return (
                <SidebarMenuItem key={item.path} data-tour={item.tourId}>
                  <SidebarMenuButton
                    tooltip={`${item.label} – Bald verfügbar`}
                    className="group/item rounded-xl opacity-50 cursor-default !h-auto !p-0"
                  >
                    <div className={cn("flex items-center w-full", isCollapsed ? "justify-center" : "gap-3 px-3 py-2")}>
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-muted/30 text-muted-foreground shrink-0">
                        <item.icon className="w-4 h-4" />
                      </div>
                      {!isCollapsed && (
                        <>
                          <span className="font-medium text-muted-foreground">{item.label}</span>
                          <span className="ml-auto text-[10px] font-semibold bg-primary/10 text-primary px-1.5 py-0.5 rounded-full whitespace-nowrap">
                            Bald
                          </span>
                        </>
                      )}
                    </div>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            }
            
            return (
              <SidebarMenuItem key={item.path} data-tour={item.tourId}>
                <SidebarMenuButton
                  asChild
                  isActive={isActive}
                  tooltip={item.label}
                  className={cn(
                    "group/item rounded-xl !h-auto !p-0",
                    isActive 
                      ? "bg-gradient-to-r from-primary/15 via-primary/10 to-transparent text-primary font-medium" 
                      : "hover:bg-primary/5"
                  )}
                >
                  <NavLink to={item.path} className={cn("flex items-center", isCollapsed ? "justify-center" : "gap-3 px-3 py-2")}>
                    <div className={cn(
                      "w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 shrink-0",
                      isActive 
                        ? "bg-gradient-to-br from-primary to-primary/80 text-primary-foreground shadow-lg shadow-primary/40" 
                        : "bg-primary/[0.08] text-primary/70 ring-1 ring-primary/10 group-hover/item:bg-primary/15 group-hover/item:text-primary group-hover/item:ring-primary/25"
                    )}>
                      <item.icon className="w-4 h-4" />
                    </div>
                    {!isCollapsed && <span className="font-medium">{item.label}</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
        </div>


        {/* Dynamic Learning Areas - Show ALL with lock icons for inaccessible */}
        {learningAreasWithAccess && learningAreasWithAccess.length > 0 && (() => {
          const liederwerkstattAreas = learningAreasWithAccess.filter(area => area.slug.startsWith('liederwerkstatt-'));
          const regularAreas = learningAreasWithAccess.filter(area => !area.slug.startsWith('liederwerkstatt-'));
          const accessibleLiederwerkstattAreas = liederwerkstattAreas.filter(area => area.hasAccess);
          const isAnyLiederwerkstattActive = liederwerkstattAreas.some(area => 
            location.pathname === `/mein-bereich/bereich/${area.slug}`
          );
          const hasAnyLiederwerkstattAccess = accessibleLiederwerkstattAreas.length > 0;
          
          return (
            <div className={cn(!isCollapsed && "sidebar-section-card")}>
              <SidebarMenu className="space-y-0.5">

                {regularAreas.map((area) => {
                  const areaPath = `/mein-bereich/bereich/${area.slug}`;
                  const isActive = location.pathname === areaPath;
                  const isLocked = !area.hasAccess;
                  
                    const sidebarLabel = (area as { sidebarLabel?: string }).sidebarLabel || area.title;
                    if (isLocked) {
                      return (
                        <SidebarMenuItem key={area.id}>
                          <SidebarMenuButton
                            tooltip={`${sidebarLabel} (Upgrade benötigt)`}
                            onClick={() => navigate(`/mein-bereich/bereich/${area.slug}`)}
                            className="group/area rounded-xl opacity-60 hover:opacity-80 cursor-pointer !h-auto !p-0"
                          >
                            <div className={cn("flex items-center w-full", isCollapsed ? "justify-center" : "gap-3 px-3 py-2.5")}>
                              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-muted/30 text-muted-foreground relative shrink-0">
                                <DynamicIcon name={area.icon} className="w-4 h-4" />
                                <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-muted flex items-center justify-center">
                                  <Lock className="w-2.5 h-2.5 text-muted-foreground" />
                                </div>
                              </div>
                              {!isCollapsed && <span className="truncate font-medium text-muted-foreground">{sidebarLabel}</span>}
                            </div>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      );
                    }
                  
                  return (
                    <SidebarMenuItem key={area.id}>
                      <SidebarMenuButton
                        asChild
                        isActive={isActive}
                        tooltip={sidebarLabel}
                        className={cn(
                          "group/area rounded-xl !h-auto !p-0",
                          isActive 
                            ? "bg-gradient-to-r from-primary/15 via-primary/10 to-transparent text-primary font-medium" 
                            : "hover:bg-primary/5"
                        )}
                      >
                        <NavLink to={areaPath} className={cn("flex items-center", isCollapsed ? "justify-center" : "gap-3 px-3 py-2")}>
                          <div className={cn(
                            "w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 shrink-0",
                            isActive 
                              ? "bg-gradient-to-br from-primary to-primary/80 text-primary-foreground shadow-lg shadow-primary/40" 
                              : "bg-primary/[0.08] text-primary/70 ring-1 ring-primary/10 group-hover/area:bg-primary/15 group-hover/area:text-primary group-hover/area:ring-primary/25"
                          )}>
                            <DynamicIcon name={area.icon} className="w-4 h-4" />
                          </div>
                          {!isCollapsed && <span className="truncate font-medium">{sidebarLabel}</span>}
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
                
                {/* Liederwerkstatt Dropdown - show if any exist */}
                {liederwerkstattAreas.length > 0 && (
                  <Collapsible defaultOpen={isAnyLiederwerkstattActive} className="group/liederwerkstatt">
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        asChild
                        isActive={isAnyLiederwerkstattActive}
                        tooltip="Liederwerkstatt"
                        className={cn(
                          "group/lw-btn rounded-xl",
                          isAnyLiederwerkstattActive 
                            ? "bg-gradient-to-r from-primary/15 via-primary/10 to-transparent text-primary font-medium" 
                            : hasAnyLiederwerkstattAccess 
                              ? "hover:bg-primary/5"
                              : "opacity-60 hover:opacity-80"
                        )}
                      >
                        <CollapsibleTrigger className={cn("flex items-center", isCollapsed ? "justify-center" : "gap-3 px-3 py-2")}>
                          <div className={cn(
                            "w-8 h-8 rounded-lg flex items-center justify-center relative shrink-0 transition-all duration-200",
                            isAnyLiederwerkstattActive 
                              ? "bg-gradient-to-br from-primary to-primary/80 text-primary-foreground" 
                              : hasAnyLiederwerkstattAccess
                                ? "bg-primary/[0.08] text-primary/70 ring-1 ring-primary/10 group-hover/lw-btn:bg-primary/15 group-hover/lw-btn:text-primary group-hover/lw-btn:ring-primary/25"
                                : "bg-muted/30 text-muted-foreground"
                          )}>
                            <Music className="w-4 h-4" />
                            {!hasAnyLiederwerkstattAccess && (
                              <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-muted flex items-center justify-center">
                                <Lock className="w-2.5 h-2.5 text-muted-foreground" />
                              </div>
                            )}
                          </div>
                          {!isCollapsed && (
                            <>
                              <span className={cn("flex-1 font-medium truncate text-left", !hasAnyLiederwerkstattAccess && "text-muted-foreground")}>Liederwerkstatt</span>
                              <ChevronDown className="w-4 h-4 shrink-0 group-data-[state=open]/liederwerkstatt:rotate-180" />
                            </>
                          )}
                        </CollapsibleTrigger>
                      </SidebarMenuButton>
                      {!isCollapsed && <CollapsibleContent>
                        <SidebarMenuSub className="ml-6 mt-1 space-y-0.5 border-l-2 border-primary/10 pl-3">
                          {liederwerkstattAreas.map((area) => {
                            const areaPath = `/mein-bereich/bereich/${area.slug}`;
                            const isActive = location.pathname === areaPath;
                            const shortName = area.title.replace('Liederwerkstatt ', '');
                            const isLocked = !area.hasAccess;
                            
                            if (isLocked) {
                              return (
                                <SidebarMenuSubItem key={area.id}>
                                  <SidebarMenuSubButton
                                    onClick={() => navigate(areaPath)}
                                    className="rounded-lg opacity-60 hover:opacity-80 cursor-pointer"
                                  >
                                    <div className="flex items-center gap-2 py-2 w-full">
                                      <DynamicIcon name={area.icon} className="w-3.5 h-3.5 text-muted-foreground" />
                                      <span className="text-muted-foreground">{shortName}</span>
                                      <Lock className="w-3 h-3 text-muted-foreground ml-auto" />
                                    </div>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                              );
                            }
                            
                            return (
                              <SidebarMenuSubItem key={area.id}>
                                <SidebarMenuSubButton
                                  asChild
                                  isActive={isActive}
                                  className={cn(
                                    "rounded-lg",
                                    isActive && "bg-primary/10 text-primary font-medium"
                                  )}
                                >
                                  <NavLink to={areaPath} className="flex items-center gap-2 py-2">
                                    <DynamicIcon 
                                      name={area.icon} 
                                      className={cn("w-3.5 h-3.5", isActive && "text-primary")} 
                                    />
                                    <span>{shortName}</span>
                                  </NavLink>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            );
                          })}
                        </SidebarMenuSub>
                      </CollapsibleContent>}
                    </SidebarMenuItem>
                  </Collapsible>
                )}
              </SidebarMenu>
            </div>
          );
        })()}
        {/* Miko & Musik-Kita Section */}
        <div className={cn(!isCollapsed && "sidebar-section-card")}>
        <SidebarMenu className="space-y-0.5">

          {(() => {
            const mikoBasePath = "/mein-bereich/miko";
            const isAnyMikoActive = location.pathname.startsWith(mikoBasePath);
            
            const mikoSubItems = [
              { icon: MessageCircle, label: "Chatbot", path: "/mein-bereich/miko/chat" },
              { icon: Wrench, label: "Tools", path: "/mein-bereich/miko/tools" },
              { icon: Library, label: "Bibliothek", path: "/mein-bereich/miko/bibliothek" },
            ];
            
            return (
              <Collapsible defaultOpen={isAnyMikoActive} className="group/collapsible">
                <SidebarMenuItem data-tour="nav-miko">
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                      tooltip="Miko KI-Assistent"
                      className={cn(
                        "w-full group/miko rounded-xl",
                        isAnyMikoActive 
                          ? "bg-gradient-to-r from-indigo-500/15 via-purple-500/10 to-transparent text-indigo-600 font-medium" 
                          : "hover:bg-indigo-500/5"
                      )}
                    >
                      <div className={cn(
                        "w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden shrink-0 transition-all duration-200",
                        isAnyMikoActive 
                          ? "bg-gradient-to-br from-indigo-500 to-purple-500" 
                          : "bg-indigo-500/10 group-hover/miko:bg-indigo-500/20"
                      )}>
                        <img 
                          src={mikoCharacter} 
                          alt="Miko" 
                          className="w-6 h-6 object-contain"
                        />
                      </div>
                      {!isCollapsed && (
                        <>
                          <span className="flex-1 font-medium">Miko</span>
                          <ChevronDown className="w-4 h-4 group-data-[state=open]/collapsible:rotate-180" />
                        </>
                      )}
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  {!isCollapsed && <CollapsibleContent>
                    <SidebarMenuSub className="ml-6 mt-1 space-y-0.5 border-l-2 border-indigo-500/10 pl-3">
                      {mikoSubItems.map((item) => {
                        const isActive = location.pathname === item.path;
                        return (
                          <SidebarMenuSubItem key={item.path}>
                            <SidebarMenuSubButton
                              asChild
                              isActive={isActive}
                              className={cn(
                                "rounded-lg",
                                isActive && "bg-indigo-500/10 text-indigo-600 font-medium"
                              )}
                            >
                              <NavLink to={item.path} className="flex items-center gap-2 py-2">
                                <item.icon className={cn("w-3.5 h-3.5", isActive && "text-indigo-500")} />
                                <span>{item.label}</span>
                              </NavLink>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        );
                      })}
                    </SidebarMenuSub>
                  </CollapsibleContent>}
                </SidebarMenuItem>
              </Collapsible>
            );
          })()}
        </SidebarMenu>

        {/* Musik-Kita Special Menu Item (gleiche Karte wie Miko) */}
        <SidebarMenu className="space-y-0.5 mt-0.5">
          <SidebarMenuItem data-tour="nav-musik-kita">
            <SidebarMenuButton
              asChild
              isActive={location.pathname === "/mein-bereich/musik-kita" || location.pathname === "/mein-bereich/bereich/musik-kita"}
              tooltip={hasMusikKitaAccess ? "Musik-Kita" : "Musik-Kita entdecken"}
              className={cn(
                "group/musikkita rounded-xl",
                (location.pathname === "/mein-bereich/musik-kita" || location.pathname === "/mein-bereich/bereich/musik-kita")
                  ? "bg-gradient-to-r from-pink-500/15 via-pink-500/10 to-transparent text-pink-600 font-medium" 
                  : hasMusikKitaAccess 
                    ? "hover:bg-pink-500/5"
                    : "opacity-70 hover:opacity-90 hover:bg-pink-500/5"
              )}
            >
              <NavLink to="/mein-bereich/musik-kita" className={cn("flex items-center", isCollapsed ? "justify-center" : "gap-3 px-3 py-2")}>
                <div className="relative shrink-0">
                  <img 
                    src={musikKitaBadge} 
                    alt="Musik-Kita" 
                    className={cn(
                      "w-8 h-8 rounded-lg object-cover transition-all duration-200",
                      !hasMusikKitaAccess && "opacity-60 grayscale-[30%]"
                    )} 
                  />
                  {!hasMusikKitaAccess && (
                    <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-muted flex items-center justify-center">
                      <Lock className="w-2.5 h-2.5 text-muted-foreground" />
                    </div>
                  )}
                </div>
                {!isCollapsed && (
                  <span className={cn("font-medium", !hasMusikKitaAccess && "text-muted-foreground")}>
                    Musik-Kita
                  </span>
                )}
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        </div>
      </SidebarContent>


      <SidebarFooter className="p-2 mt-auto">
        <div className={cn(!isCollapsed && "sidebar-section-card")}>
        <SidebarMenu className="space-y-0.5">

          {accountMenuItems.map((item) => {
            const isActive = location.pathname === item.path;
            const isHelpItem = item.path === "/mein-bereich/hilfe";
            const showBadge = isHelpItem && openTicketCount > 0;

            const iconBlock = (
              <div className={cn(
                "w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 shrink-0 relative",
                isActive
                  ? "bg-gradient-to-br from-primary to-primary/80 text-primary-foreground shadow-lg shadow-primary/40"
                  : "bg-primary/[0.08] text-primary/70 ring-1 ring-primary/10 group-hover/footer:bg-primary/15 group-hover/footer:text-primary group-hover/footer:ring-primary/25"
              )}>
                <item.icon className="w-4 h-4" />
                {showBadge && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center animate-pulse">
                    {openTicketCount}
                  </span>
                )}
              </div>
            );

            return (
              <SidebarMenuItem key={item.path} data-tour={item.tourId}>
                <SidebarMenuButton
                  asChild
                  isActive={isActive}
                  tooltip={item.label}
                  className={cn(
                    "group/footer rounded-xl",
                    isActive
                      ? "bg-gradient-to-r from-primary/15 via-primary/10 to-transparent text-primary font-medium"
                      : "hover:bg-primary/5"
                  )}
                >
                  {(() => {
                    const linkClass = cn("flex items-center min-w-0 w-full", isCollapsed ? "justify-center" : "gap-2 px-3 py-2");
                    const inner = (
                      <>
                        {iconBlock}
                        {!isCollapsed && (
                          <>
                            <span className="font-medium truncate min-w-0">{item.label}</span>
                            {showBadge && (
                              <span className="ml-auto text-[10px] font-semibold bg-destructive text-destructive-foreground px-1.5 py-0.5 rounded-full whitespace-nowrap shrink-0 leading-none">
                                {openTicketCount}
                              </span>
                            )}
                          </>
                        )}
                      </>
                    );
                    return "external" in item && item.external ? (
                      <a href={item.path} target="_blank" rel="noopener noreferrer" className={linkClass}>
                        {inner}
                      </a>
                    ) : (
                      <NavLink to={item.path} className={linkClass}>
                        {inner}
                      </NavLink>
                    );
                  })()}
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
        </div>
        {/* Schalter „Neue Ansicht“ (Miko Labs): nur für freigegebene Konten, sonst nichts */}
        <NeueAnsichtKnopf kontoId={user?.id} />
        {/* App Store Badges ganz unten */}
        <AppStoreBadges isCollapsed={isCollapsed} />
      </SidebarFooter>


    </Sidebar>
  );
};

const StudentLayout = ({ children }: StudentLayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [session, setSession] = useState<Session | null>(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [authTimedOut, setAuthTimedOut] = useState(false);
  const [moreSheetOpen, setMoreSheetOpen] = useState(false);
  useDidactaTracking();

  // Impersonation state (must be declared before any conditional returns)
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatingAs, setImpersonatingAs] = useState<string | null>(null);

  useEffect(() => {
    const impersonating = safeSessionGetItem("isImpersonating") === "true";
    const impersonatingName = safeSessionGetItem("impersonatingAs");
    setIsImpersonating(impersonating);
    setImpersonatingAs(impersonatingName);
  }, []);

  const handleEndImpersonation = async () => {
    const adminReturnUrl = safeSessionGetItem("adminReturnUrl");

    // Abmelden räumt nur die Anmeldung DIESES Tabs weg – sie liegt dank der
    // Speicherschicht im sessionStorage. Die Administratorin bleibt in ihren
    // übrigen Tabs angemeldet. Erst danach die Marker löschen.
    await supabase.auth.signOut({ scope: 'local' });

    safeSessionRemoveItem("isImpersonating");
    safeSessionRemoveItem("impersonatingAs");
    safeSessionRemoveItem("adminReturnUrl");
    safeSessionRemoveItem(UEBERNAHME_MARKE);

    // Try to close the impersonation tab
    window.close();
    
    // If window.close() didn't work (e.g., tab wasn't opened by script), 
    // redirect to admin URL after a short delay
    if (adminReturnUrl) {
      setTimeout(() => {
        window.location.href = adminReturnUrl;
      }, 100);
    }
  };

  useEffect(() => {
    let isMounted = true;
    let initialSessionHandled = false;

    // Primary: rely on onAuthStateChange INITIAL_SESSION event.
    // This is the most reliable way to get the session in Supabase JS v2.x
    // as it doesn't suffer from Web Locks API issues that getSession() can have.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!isMounted) return;
        console.log('[StudentLayout] Auth event:', event, session ? 'has session' : 'no session');

        if (event === 'INITIAL_SESSION') {
          initialSessionHandled = true;
          setSession(session);
          if (!session) {
            console.warn('[StudentLayout] No session on INITIAL_SESSION, redirecting to login');
            navigate(loginMitRueckwegHierher());
          }
          setIsCheckingAuth(false);
          return;
        }

        setSession(session);
        if (event === 'SIGNED_OUT') {
          navigate(abmeldenZiel());
        }
      }
    );

    // Fallback: if INITIAL_SESSION doesn't fire within 5s (e.g., older Supabase version
    // or edge case), fall back to getSession() + getUser() as last resort.
    const fallbackTimer = setTimeout(async () => {
      if (initialSessionHandled || !isMounted) return;
      console.warn('[StudentLayout] INITIAL_SESSION did not fire within 5s, using fallback');

      try {
        // Try getSession first
        const { data: { session } } = await supabase.auth.getSession();
        if (session && isMounted) {
          console.log('[StudentLayout] Fallback: session found via getSession');
          setSession(session);
          setIsCheckingAuth(false);
          return;
        }

        // Last resort: try refreshing the session directly (avoids getUser API call that causes throttling)
        const { data: refreshData } = await supabase.auth.refreshSession();
        if (refreshData?.session && isMounted) {
          console.log('[StudentLayout] Fallback: session restored via refreshSession');
          setSession(refreshData.session);
          setIsCheckingAuth(false);
          return;
        }
      } catch (err) {
        console.error('[StudentLayout] Fallback auth check failed:', err);
      }

      if (!isMounted) return;
      console.warn('[StudentLayout] All auth methods failed, redirecting to login');
      navigate(loginMitRueckwegHierher());
      setIsCheckingAuth(false);
    }, 5000);

    return () => {
      isMounted = false;
      subscription.unsubscribe();
      clearTimeout(fallbackTimer);
    };
  }, [navigate]);

  // Safety timeout: if auth check takes too long, show a helpful message
  useEffect(() => {
    if (!isCheckingAuth) return;
    const timer = setTimeout(() => {
      console.warn('[StudentLayout] Auth check timed out after 8s');
      setAuthTimedOut(true);
    }, 8000);
    return () => clearTimeout(timer);
  }, [isCheckingAuth]);

  // Use AccessContext instead of redundant RPC calls - saves 3 has_role + 1 getUser call
  const accessContext = useAccess();
  const isAdmin = accessContext.isAdmin;
  const openTicketCountMobile = useOpenTickets();
  const neueAnsicht = useNeueAnsicht(session?.user?.id);
  // Den Punkt „neue Beiträge“ zeigt nur die gewohnte Leiste: Unter der neuen Ansicht fragt niemand jede Minute.
  const { hasUnread: hasUnreadCommunity, markAsRead: markCommunityAsRead } = useUnreadCommunityPosts(!!session && !neueAnsicht.aktiv);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    toast.success("Erfolgreich abgemeldet");
    navigate(abmeldenZiel());
  };

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-3">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          {authTimedOut && (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Die Verbindung dauert ungewöhnlich lange.
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => window.location.reload()}
              >
                Seite neu laden
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-3">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-sm text-muted-foreground">Nicht angemeldet – Weiterleitung…</p>
        </div>
      </div>
    );
  }

  // Weiche zur neuen Ansicht (Miko Labs, docs/labs.md): gleiche Seite, neue Hülle.
  if (neueAnsicht.aktiv) return <NeueAnsicht>{children}</NeueAnsicht>;
  

  const bottomNavItems = [
    { icon: Home, label: "Übersicht", path: "/mein-bereich" },
    { icon: BookOpen, label: "Kurse", path: "/mein-bereich/kurse" },
    { icon: Users, label: "Community", path: "/mein-bereich/community" },
    { icon: MessageCircle, label: "Miko", path: "/mein-bereich/miko/chat" },
  ];
  
  
  const moreMenuItems = [
    { icon: Calendar, label: "Meine Termine", path: "/mein-bereich/termine" },
    { icon: Heart, label: "Favoriten & Notizen", path: "/mein-bereich/favoriten" },
    { icon: Award, label: "Meine Zertifikate", path: "/mein-bereich/zertifikate" },
    { icon: HelpCircle, label: "Hilfe & Support", path: "/mein-bereich/hilfe" },
  ];

  return (
    <SidebarProvider>
        {/* Impersonation Banner */}
        {isImpersonating && (
          <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-amber-950 px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              <span className="text-sm font-medium">
                Admin-Ansicht als: <strong>{impersonatingAs || "Nutzer"}</strong>
              </span>
            </div>
            <Button 
              variant="secondary" 
              size="sm" 
              onClick={handleEndImpersonation}
              className="bg-amber-600 hover:bg-amber-700 text-white border-0 h-7 px-3 text-xs"
            >
              Beenden & Zurück
            </Button>
          </div>
        )}
        <div className={cn("min-h-screen flex w-full bg-slate-50", isImpersonating && "pt-10")}>
          <StudentSidebar />
          <main className="flex-1 overflow-auto pb-20 md:pb-0">
            <div className="flex-1 flex flex-col">
              <header className="h-14 md:h-14 border-b border-border/30 bg-white shadow-sm flex items-center justify-between px-2 md:px-4 sticky top-0 z-10">
                <div className="flex items-center gap-2">
                  <SidebarTrigger className="h-10 w-10 md:h-10 md:w-10" />
                  {/* Mobile Tier Badge */}
                  <TierBadge />
                </div>
                
                <div className="flex items-center gap-1 md:gap-2">
                  {isAdmin && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => window.location.href = '/admin/dashboard'}
                      className="gap-2 h-8 md:h-9 px-2 md:px-3"
                    >
                      <Shield className="h-4 w-4" />
                      <span className="hidden sm:inline">Admin-Ansicht</span>
                    </Button>
                  )}
                  
                  {/* Mobile: Info + Dashboard-Config + Search + Avatar */}
                  <div className="flex items-center gap-1 md:hidden">
                    {/* Info Popover for Welcome Text */}
                    <WelcomeInfoPopover />

                    {/* Dashboard Config Icon */}
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => navigate('/mein-bereich?config=true')}
                      className="h-10 w-10 p-0"
                    >
                      <Settings2 className="h-5 w-5 text-muted-foreground" />
                    </Button>
                    
                    {/* Search - larger icon */}
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => navigate('/mein-bereich/suche')}
                      className="h-10 w-10 p-0"
                    >
                      <Search className="h-7 w-7 text-muted-foreground" />
                    </Button>

                    {/* Settings/Avatar */}
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => navigate('/mein-bereich/einstellungen')}
                      className="h-10 w-10 p-0"
                    >
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-primary-foreground text-xs font-bold">
                        <Settings className="h-4 w-4" />
                      </div>
                    </Button>
                  </div>
                  
                  {/* Desktop: Logout button */}
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={handleLogout}
                    className="gap-2 text-muted-foreground hover:text-destructive h-8 md:h-9 px-2 md:px-3 hidden md:flex"
                  >
                    <LogOut className="h-4 w-4" />
                    <span className="hidden sm:inline">Abmelden</span>
                  </Button>
                </div>
              </header>
              <div className="p-4 pb-24 md:p-6 md:pb-24 lg:p-8 lg:pb-24">
                {children}
              </div>
            </div>
          </main>
          
          {/* Floating Miko Chat Bubble */}
          <MikoFloatingBubble />
          
          {/* Bottom Navigation for Mobile - Türkis Gradient with Gold accents */}
          <nav className="fixed bottom-0 left-0 right-0 h-20 bg-white flex items-center justify-around px-2 md:hidden z-50 safe-area-bottom shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
            {bottomNavItems.map((item) => {
              const isActive = item.path === "/mein-bereich" 
                ? location.pathname === item.path
                : location.pathname.startsWith(item.path);
              const isCommunity = item.path === "/mein-bereich/community";
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => {
                    if (isCommunity) markCommunityAsRead();
                  }}
                  className={cn(
                    "flex flex-col items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl transition-all min-w-[60px] relative",
                    isActive 
                      ? "bg-primary/10" 
                      : "hover:bg-muted/50"
                  )}
                >
                  <div className="relative">
                    <item.icon className={cn("h-6 w-6", isActive ? "text-primary" : "text-muted-foreground")} />
                    {isCommunity && hasUnreadCommunity && !isActive && (
                      <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-accent rounded-full border-2 border-white" />
                    )}
                  </div>
                  <span className={cn("text-xs font-semibold", isActive ? "text-primary" : "text-muted-foreground")}>{item.label}</span>
                </NavLink>
              );
            })}
            
            {/* "Mehr" Button with Sheet */}
            <Sheet open={moreSheetOpen} onOpenChange={setMoreSheetOpen}>
              <SheetTrigger asChild>
                <button
                  className={cn(
                    "flex flex-col items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl transition-all min-w-[60px] relative",
                    moreSheetOpen 
                      ? "bg-primary/10" 
                      : "hover:bg-muted/50"
                  )}
                >
                  <div className="relative">
                    <MoreHorizontal className={cn("h-6 w-6", moreSheetOpen ? "text-primary" : "text-muted-foreground")} />
                    {openTicketCountMobile > 0 && (
                      <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-destructive rounded-full border-2 border-white" />
                    )}
                  </div>
                  <span className={cn("text-xs font-semibold", moreSheetOpen ? "text-primary" : "text-muted-foreground")}>Mehr</span>
                </button>
              </SheetTrigger>
              <SheetContent side="bottom" className="h-auto max-h-[70vh] rounded-t-2xl">
                <SheetHeader className="pb-4">
                  <SheetTitle className="text-center">Weitere Optionen</SheetTitle>
                </SheetHeader>
                <div className="grid grid-cols-2 gap-3 pb-6">
                  {moreMenuItems.map((item) => {
                    const isActive = location.pathname === item.path;
                    const isHelpItem = item.path === "/mein-bereich/hilfe";
                    const showBadge = isHelpItem && openTicketCountMobile > 0;
                    return (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        onClick={() => setMoreSheetOpen(false)}
                        className={cn(
                          "flex flex-col items-center justify-center gap-2 p-4 rounded-xl border transition-all relative",
                          isActive 
                            ? "bg-primary/10 border-primary/30 text-primary" 
                            : "bg-muted/30 border-border hover:bg-muted/50"
                        )}
                      >
                        <div className={cn(
                          "w-12 h-12 rounded-full flex items-center justify-center relative",
                          isActive ? "bg-primary text-primary-foreground" : "bg-muted"
                        )}>
                          <item.icon className="h-6 w-6" />
                          {showBadge && (
                            <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">
                              {openTicketCountMobile}
                            </span>
                          )}
                        </div>
                        <span className="text-sm font-medium text-center">{item.label}</span>
                      </NavLink>
                    );
                  })}
                </div>
                
                {/* Logout Button in Sheet */}
                <div className="border-t pt-4">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setMoreSheetOpen(false);
                      handleLogout();
                    }}
                    className="w-full justify-center gap-2 text-muted-foreground hover:text-destructive"
                  >
                    <LogOut className="h-5 w-5" />
                    Abmelden
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </nav>
        </div>
      </SidebarProvider>
  );
};

export default StudentLayout;
